import { keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { PlusIcon, UsersIcon } from 'lucide-react';
import { Link } from 'react-router';

import type { AccountDTO } from '@invenco/common-interface/accounts';

import { Button } from '@/components/core/button';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages, formatDateTime } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';

const columnHelper = createColumnHelper<AccountDTO>();

const columns = [
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('primaryName', { header: 'Primary contact' }),
  columnHelper.accessor('primaryEmail', { header: 'Primary email' }),
  columnHelper.accessor('primaryPhone', { header: 'Primary phone' }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    cell: ({ getValue }) => formatDateTime(getValue()),
  }),
];

export function AccountList() {
  const { accountsGateway } = useGateways();
  const onClickRow = useRowNavigationCallback<AccountDTO>('/accounts');

  const { state, filters, onPaginationChange, onSortingChange } = useDataTableState({
    columns,
  });

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'accounts',
    input: { search: filters.search },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getAccounts({ search: input.search, cursor, take }, { signal }),
      ),
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    data,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    onPaginationChange,
    onSortingChange,
    state,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <UsersIcon />
        </HeaderIcon>
        <HeaderTitle>Accounts</HeaderTitle>
        <HeaderActions>
          <Button asChild>
            <Link to="/accounts/new">
              <PlusIcon />
              Create
            </Link>
          </Button>
        </HeaderActions>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
